import { Autocomplete, Avatar, Box, TextField } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "../lib/supabase";

// Role-filtered assignee picker for a deal's AE / BDR slot. Fed by
// manna_list_users_with_role so only users holding the role appear. Deep module:
// callers pass a role + value and never touch the query.
// SPEC: docs/specs/2026-06-03-deal-ae-bdr-assignment-design.md.

export interface RepOption {
  id: string;
  email: string;
  avatar_url: string | null;
  full_name: string | null;
}

export function repLabel(o: RepOption): string {
  return o.full_name?.trim() || o.email;
}

function useUsersWithRole(role: "ae" | "bdr", enabled: boolean, territoryId?: string | null) {
  return useQuery({
    // territoryId is part of the key so the list refetches when the deal's
    // territory changes (the cascade narrows reps to that territory's people).
    queryKey: ["users_with_role", role, territoryId ?? null],
    enabled,
    staleTime: 60_000,
    queryFn: async (): Promise<RepOption[]> => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data, error } = await (supabase.rpc as any)("manna_list_users_with_role", {
        p_role: role,
        p_territory_id: territoryId ?? null,
      });
      if (error) throw error;
      return (data ?? []) as RepOption[];
    },
  });
}

export interface RepPickerProps {
  role: "ae" | "bdr";
  label: string;
  value: string | null;
  onChange: (id: string | null) => void;
  disabled?: boolean;
  /** When set, narrows the list to reps eligible for this territory
   *  (global reps + reps scoped to the territory or an ancestor). */
  territoryId?: string | null;
}

export function RepPicker({ role, label, value, onChange, disabled, territoryId }: RepPickerProps) {
  // Fetch options whenever the picker is editable OR there's a value to resolve
  // to a display label (read-only display still needs the name).
  const { data: options = [], isLoading } = useUsersWithRole(
    role,
    !disabled || value != null,
    territoryId,
  );
  const selected = options.find((o) => o.id === value) ?? null;

  return (
    <Autocomplete<RepOption>
      options={options}
      loading={isLoading}
      value={selected}
      disabled={disabled}
      onChange={(_, v) => onChange(v?.id ?? null)}
      getOptionLabel={repLabel}
      isOptionEqualToValue={(a, b) => a.id === b.id}
      renderOption={(props, o) => (
        <Box
          component="li"
          {...props}
          key={o.id}
          sx={{ display: "flex", gap: 1, alignItems: "center" }}
        >
          <Avatar src={o.avatar_url ?? undefined} sx={{ width: 24, height: 24, fontSize: 12 }}>
            {(repLabel(o)[0] ?? "?").toUpperCase()}
          </Avatar>
          {repLabel(o)}
        </Box>
      )}
      renderInput={(params) => <TextField {...params} label={label} />}
      fullWidth
      size="small"
    />
  );
}
